// SQ-AI — CEO-facing conversational page for the chart-mode agent.
//
// Single entry point for the lab (per CLAUDE.md). The route /sq-ai mounts this
// component; everything else under labs/sq-ai/ is internal.
//
// Presentation is rebuilt to the design mocks (sq_context_picker_mock.html +
// sq_thinking_status_mock.html) via sqAi.module.css — the purple token set, the
// input box, chips, user bubbles, and the streaming "Thinking…" status. The data
// layer (socket, usePicker, /context endpoints) is unchanged. Context carries
// across turns on one conversationId; changing env / tenant / confidential access
// / timezone starts a new chat because context across scopes makes no sense.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, MessageSquarePlus, SlidersHorizontal } from "lucide-react";
import { tenantApi, aiAssistantApi } from "../../services/api";
import { getSocket, setAuth, refreshAuth } from "./sqAiSocket";
import { useSqAiConversation } from "./useSqAiConversation";
import { usePicker } from "./usePicker";
import styles from "./sqAi.module.css";
import { Sparkle } from "./components/icons";
import Composer from "./components/Composer";
import ContextPicker from "./components/ContextPicker";
import Thread from "./components/Thread";
import EmptyState from "./components/EmptyState";
import SettingsBar from "./components/SettingsBar";

function newConversationId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "sq-" + Date.now() + "-" + Math.floor(Math.random() * 1000000);
}

export default function SqAiPage() {
  // Sandbox-local state. Deliberately NOT persisted to the same localStorage
  // keys the chart-agent dev UI uses, so toggling here doesn't affect the dev
  // UI (and vice versa). Defaults: prod env (the CEO wants real data),
  // confidential off (fail-closed).
  const [env, setEnv] = useState("prod");
  const [tenants, setTenants] = useState([]);
  const [tenantSchema, setTenantSchema] = useState("");
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);
  const [confidentialAccess, setConfidentialAccess] = useState(false);
  // Querying user's IANA timezone (sandbox dropdown). The agent converts UTC→this zone and
  // truncates to date for all date logic, matching the dashboard. Default Asia/Calcutta matches
  // the known SafetyQube token; in production the token's selectedTimezone claim is authoritative.
  const [userTz, setUserTz] = useState("Asia/Calcutta");

  const [conversationId, setConversationId] = useState(() => newConversationId());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Initial mount: stamp the auth values onto the socket service BEFORE
  // creating the socket so the first handshake carries the right env/conf.
  useEffect(() => {
    setAuth({ env, confidentialAccess, userTz });
    const s = getSocket();
    socketRef.current = s;
    const onConnect = () => { setConnected(true); setConnectionError(null); };
    const onDisconnect = () => setConnected(false);
    const onConnectError = (err) => {
      setConnected(false);
      setConnectionError({ code: (err.data && err.data.code) || "connect_error", message: err.message });
    };
    if (s.connected) setConnected(true);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("connect_error", onConnectError);
    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("connect_error", onConnectError);
    };
    // eslint-disable-next-line
  }, []);

  // Re-handshake when env / confidentialAccess / userTz changes. The /ai-assistant
  // namespace reads them off the handshake, so changing them mid-session
  // requires a reconnect.
  useEffect(() => {
    setAuth({ env, confidentialAccess, userTz });
    refreshAuth();
    // eslint-disable-next-line
  }, [env, confidentialAccess, userTz]);

  // Tenant list. Reuses tenantApi (the same source the chart-agent dev UI
  // uses) so the available-tenants set is consistent across both pages.
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    setTenantsError(null);
    tenantApi
      .getAllForEnv(env)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data.tenants || [];
        setTenants(list);
        setTenantsLoading(false);
        const stillValid = tenantSchema && list.some((t) => (t.name || t.schema || t) === tenantSchema);
        if (!stillValid) setTenantSchema(list[0] ? list[0].name || list[0].schema || list[0] : "");
      })
      .catch((err) => {
        if (cancelled) return;
        setTenantsError(err.message || "Could not load tenants");
        setTenantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // tenantSchema deliberately excluded — we only refetch when env changes.
    // eslint-disable-next-line
  }, [env]);

  // Tenant terminology for the empty-state chips. Cached per (env, tenant)
  // for the session; a failed fetch just leaves `terms` null and the chips
  // fall back to generic wording — the empty state never blocks on this.
  const termsCacheRef = useRef(new Map());
  const [terms, setTerms] = useState(null);
  useEffect(() => {
    if (!tenantSchema) {
      setTerms(null);
      return;
    }
    const key = `${env}:${tenantSchema}`;
    const cached = termsCacheRef.current.get(key);
    if (cached) {
      setTerms(cached);
      return;
    }
    let cancelled = false;
    setTerms(null);
    aiAssistantApi
      .getTenantTerminology(env, tenantSchema)
      .then((res) => {
        if (cancelled) return;
        const t = res.data && res.data.terms;
        if (t) {
          termsCacheRef.current.set(key, t);
          setTerms(t);
        }
      })
      .catch(() => {
        /* generic chips are the fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [env, tenantSchema]);

  // Context picker scope (modules + templates the user pins). Lives at the
  // chat-session level so it persists across turns; resetKey=conversationId
  // clears it on a new chat or any scope change (which rotates conversationId).
  const picker = usePicker({ env, tenantSchema, resetKey: conversationId });

  const conv = useSqAiConversation({
    socket: socketRef.current,
    conversationId,
    tenantSchema,
    context: picker.context,
  });

  const hasMessages = conv.state.messages.length > 0;
  const isInFlight = conv.state.status === "in_flight";

  // #2a: when the user asks to COMPARE a second module ("compare X and Y"), the
  // backend broadens the scope and emits ai:context_update so the new chip appears
  // in the picker (the user sees the change). Handled here where usePicker lives.
  // A ref keeps the listener stable without re-subscribing every picker change.
  const ctxUpdateRef = useRef({ picker, conversationId });
  ctxUpdateRef.current = { picker, conversationId };
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;
    const onContextUpdate = (p) => {
      const cur = ctxUpdateRef.current;
      if (!p || p.conversationId !== cur.conversationId) return;
      if (p.action === "add" && Array.isArray(p.modules)) {
        // Atomic multi-add (one setSelected) so a 2+ module payload can't drop chips.
        cur.picker.addModules(p.modules.map((m) => m && m.canonical).filter(Boolean));
        if (p.note) setNotice(p.note); // surface the backend's "Added X — comparing…" line
      }
    };
    s.on("ai:context_update", onContextUpdate);
    return () => s.off("ai:context_update", onContextUpdate);
  }, [conversationId]);

  const startNewChat = useCallback((noticeText) => {
    setConversationId(newConversationId());
    setNotice(noticeText || null);
  }, []);

  // Scope changes invalidate the conversation context. The reconnect that
  // env/conf changes trigger also drops the server's in-memory conversation
  // state, so a fresh conversationId is required, not just nice-to-have.
  const handleEnvChange = (next) => {
    if (next === env) return;
    setEnv(next);
    startNewChat(hasMessages ? "Environment changed — started a new chat." : null);
  };

  const handleTenantChange = (next) => {
    if (next === tenantSchema) return;
    setTenantSchema(next);
    startNewChat(hasMessages ? "Tenant changed — started a new chat." : null);
  };

  const handleConfidentialChange = (next) => {
    setConfidentialAccess(next);
    startNewChat(hasMessages ? "Access settings changed — started a new chat." : null);
  };

  const handleUserTzChange = (next) => {
    if (next === userTz) return;
    setUserTz(next);
    startNewChat(hasMessages ? "Timezone changed — started a new chat." : null);
  };

  const handleSend = (text) => {
    setNotice(null);
    conv.send(text);
  };

  if (connectionError) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          <div className={styles.emptyMark}>
            <AlertTriangle className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <h2 className={styles.emptyTitle}>SQ-AI is unavailable</h2>
          <p className={styles.emptySub}>{connectionError.message}</p>
          <p className={styles.settingsHint}>{connectionError.code}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.column}>
          <div className={styles.headerInner}>
            <div className={styles.brand}>
              <span className={styles.brandMark}>
                <Sparkle />
              </span>
              <span className={styles.brandName}>SafetyQube AI</span>
            </div>
            <div className={styles.headerControls}>
              <span
                className={`${styles.statusDot} ${connected ? styles.statusDotOn : ""}`}
                title={connected ? "Connected" : "Connecting…"}
              />
              <span className={styles.scopeText}>
                {env}
                {tenantSchema ? ` · ${tenantSchema}` : ""}
              </span>
              <button
                type="button"
                className={`${styles.ghostBtn} ${settingsOpen ? styles.ghostBtnActive : ""}`}
                onClick={() => setSettingsOpen((v) => !v)}
                title="Scope settings"
                aria-pressed={settingsOpen}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>Settings</span>
              </button>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => startNewChat(null)}
                disabled={isInFlight || !hasMessages}
                title="New chat"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>New chat</span>
              </button>
            </div>
          </div>
        </div>
        {settingsOpen && (
          <SettingsBar
            env={env}
            onEnvChange={handleEnvChange}
            tenants={tenants}
            tenantSchema={tenantSchema}
            onTenantChange={handleTenantChange}
            tenantsLoading={tenantsLoading}
            tenantsError={tenantsError}
            confidentialAccess={confidentialAccess}
            onConfidentialChange={handleConfidentialChange}
            userTz={userTz}
            onUserTzChange={handleUserTzChange}
            disabled={isInFlight}
          />
        )}
      </header>

      <main className={styles.scroll}>
        {hasMessages ? (
          <div className={styles.column}>
            {notice && <p className={styles.notice}>{notice}</p>}
            <Thread
              messages={conv.state.messages}
              onSuggestionClick={handleSend}
              suggestionsDisabled={isInFlight || !connected || !tenantSchema}
            />
          </div>
        ) : (
          <div className={styles.columnFill}>
            {notice && <p className={styles.notice}>{notice}</p>}
            <EmptyState onSelectExample={handleSend} disabled={!connected || !tenantSchema} terms={terms} />
          </div>
        )}
      </main>

      <footer>
        <div className={styles.column}>
          <Composer
            onSubmit={handleSend}
            onCancel={conv.cancel}
            isInFlight={isInFlight}
            disabled={!connected || !tenantSchema}
            note={picker.note}
            onDismissNote={picker.dismissNote}
            picker={
              <ContextPicker
                available={picker.available}
                availableLoading={picker.availableLoading}
                availableError={picker.availableError}
                selected={picker.selected}
                templates={picker.templates}
                toggleModule={picker.toggleModule}
                removeModule={picker.removeModule}
                onTemplatesChange={picker.setTemplates}
                loadTemplates={picker.loadTemplates}
                getTemplateEntry={picker.getTemplateEntry}
                disabled={!connected || !tenantSchema}
              />
            }
          />
        </div>
      </footer>
    </div>
  );
}
